import { createContext, use } from "react";

import type { MissingInfoRow, FieldOverrides } from "./missingInfo";
import type { ReaderReport } from "../../api/models/bid";
import type { CalculateJsonResult } from "../../hooks/useCalculate";

// Shared state bridge for the "complete missing info" review surface (7HAI-369).
//
// Mirrors RowReviewContext (7HAI-349): after a CAD calc that came back with
// flagged rows (missing_reason / material_defaulted), CalculateFileCard PUBLISHES
// the flagged rows + a recalc callback here; the dedicated MissingInfoPage (a
// sibling route) CONSUMES them, renders the per-field editors + bulk-assign,
// gathers the user's per-row completions and re-runs the calc through the
// callback. On a deep link / refresh (the card's in-memory result is gone) the
// payload is null and the page shows a "run a calculation first" empty state.
//
// Context + hook + types live in this `.ts` module (not the provider `.tsx`) so
// the provider file only exports a component — keeps react-refresh happy.

export interface MissingInfoPayload {
  // The flagged rows from the latest calc (only rows needing attention).
  rows: MissingInfoRow[];
  // Total rows the calc produced (for the "N of M parts need info" summary).
  totalRows: number;
  // Source file name (shown in the header).
  fileName: string | null;
  // The exact File the calc consumed + the CAD discriminator defaults it ran
  // with (7HAI-388). Carried through the context so that when the review hands
  // back "all resolved", the card — which REMOUNTS on the navigation back to
  // /calculate and so has lost its own `file`/defaults useState — can rehydrate
  // them and present the save flow. Without this the user lands on a save panel
  // whose file is null: a dead/empty calculate page.
  file: File;
  defaultStandard: string;
  defaultRating: string;
  defaultMaterialFamily: string;
  // "Set all rows to material" from the card — carried like the CAD defaults so
  // the remounted card keeps calculating every row as the chosen material.
  forceMaterialFamily?: string;
  forceMaterialGrade?: string;
  // 7HAI-1449: what the CAD reader had to say about the upload, shown on the
  // review so the estimator sees "spool 103 is not in this zip" next to the
  // parts it could not price. Optional: older publishers omit it.
  readerReport?: ReaderReport | null;
  // Re-run the calc with the user's per-row completions. Resolves with the
  // fresh JSON result so the page can refresh the flagged list (resolved rows
  // drop off). Owned by the card so the calc plumbing stays in one place. The
  // recalc fn is built from the card's `file` + defaults, captured into the
  // payload while the card is mounted, so it stays callable after the card
  // unmounts (it closes over the file/session, not the card's React state).
  recalculate: (overrides: FieldOverrides) => Promise<CalculateJsonResult>;
}

// The completed result handed back to the calc card once every flagged row is
// resolved. Lives in the CONTEXT (not the card) because the card unmounts while
// the review page is open — a callback into the card's setState would no-op.
// The card reads this on remount to show the "save the bid" panel and to thread
// the completions into the persisted bid.
export interface MissingInfoResolved {
  overrides: FieldOverrides;
  // The file + CAD defaults the calc ran with (7HAI-388), threaded back from the
  // payload so the remounted card can rehydrate them and open the save flow with
  // a real file (not a null one). The card has no other way to recover these:
  // its own `file`/defaults useState is wiped when it unmounts during review.
  file: File;
  defaultStandard: string;
  defaultRating: string;
  defaultMaterialFamily: string;
  // "Set all rows to material" from the card — carried like the CAD defaults so
  // the remounted card keeps calculating every row as the chosen material.
  forceMaterialFamily?: string;
  forceMaterialGrade?: string;
  // 7HAI-1437: how many parts the review left UNPRICED — the rows the backend
  // still could not price with the information given (skipped, or finished
  // with "Continue with current values"). 0 means every part priced. The card
  // uses it to say so honestly instead of "All parts priced".
  unpricedCount: number;
  // 7HAI-1449: threaded back so the remounted card can show the report again.
  readerReport?: ReaderReport | null;
}

export interface MissingInfoContextValue {
  payload: MissingInfoPayload | null;
  setPayload: (payload: MissingInfoPayload | null) => void;
  // Set by the review page when all rows resolve; read + cleared by the card.
  resolved: MissingInfoResolved | null;
  setResolved: (resolved: MissingInfoResolved | null) => void;
}

export const MissingInfoContext = createContext<MissingInfoContextValue | undefined>(undefined);

// Read the missing-info bridge. Returns a no-op fallback when used outside a
// provider so a stray render never throws — it publishes into the void.
export const useMissingInfo = (): MissingInfoContextValue => {
  const ctx = use(MissingInfoContext);
  if (!ctx) {
    return { payload: null, setPayload: () => {}, resolved: null, setResolved: () => {} };
  }
  return ctx;
};
